import {
  Bookmark,
  GlassWater,
  IceCreamCone,
  Minus,
  Plus,
  Salad,
  ShoppingCart,
  Trash2,
  UtensilsCrossed,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/lib/format-price";
import { lineSubtotal, lineUnitPrice } from "@/lib/cart-store";
import type { MenuSection, PublicMenuProduct } from "@/types/menu";
import { buildSupermarketCatalog, type SupermarketCategory } from "../menu/public-menu";
import { DeliveryLoginCta } from "./delivery-login-cta";
import {
  type FulfillmentType,
  fulfillmentTypeLabelKey,
  type PublicMenuFlowModel,
  type PublicMenuFlowVariant,
} from "./public-menu-flow-model";
import type { PublicMenuFlowActions, PublicMenuFlowState } from "./use-public-menu-flow";

const UPLOADTHING_BASE_URL = "https://paj514wbqe.ufs.sh/f";

const SECTION_ICONS: Record<string, typeof UtensilsCrossed> = {
  starters: Salad,
  entradas: Salad,
  mains: UtensilsCrossed,
  principales: UtensilsCrossed,
  desserts: IceCreamCone,
  postres: IceCreamCone,
  drinks: GlassWater,
  bebidas: GlassWater,
};

function getSectionIcon(slug: string) {
  const normalised = slug.toLowerCase();
  for (const [key, Icon] of Object.entries(SECTION_ICONS)) {
    if (normalised.includes(key)) {
      return Icon;
    }
  }
  return UtensilsCrossed;
}

function ProductCard({
  variant,
  product,
  currency,
  canOrder,
  quantity,
  onAdd,
  onDecrement,
}: {
  variant: PublicMenuFlowVariant;
  product: PublicMenuProduct;
  currency: string | null;
  canOrder: boolean;
  quantity: number;
  onAdd: (product: PublicMenuProduct) => void;
  onDecrement: (productId: string) => void;
}) {
  const { t } = useTranslation();
  const hasPromotion =
    product.discountPercentage !== null &&
    product.discountPercentage > 0 &&
    product.discountedPrice !== null;
  const promotionMinQuantity =
    hasPromotion && product.promotionMinQuantity ? product.promotionMinQuantity : null;
  const isUnavailable = !product.available;
  const inCart = quantity > 0;
  const unavailableLabel =
    variant === "delivery"
      ? t("delivery.menu.unavailable", { defaultValue: "Sin stock" })
      : t("menu.public.unavailable", { defaultValue: "Sin stock" });
  const removeLabel =
    variant === "delivery"
      ? t("delivery.cart.remove", { defaultValue: "Quitar" })
      : t("menu.public.cart.remove", { defaultValue: "Quitar" });
  const decrementLabel =
    variant === "delivery"
      ? t("delivery.cart.decrement", { defaultValue: "Restar" })
      : t("menu.public.cart.decrement", { defaultValue: "Restar" });
  const incrementLabel =
    variant === "delivery"
      ? t("delivery.cart.increment", { defaultValue: "Sumar" })
      : t("menu.public.cart.increment", { defaultValue: "Sumar" });
  const addLabel =
    variant === "delivery"
      ? t("delivery.menu.add", { defaultValue: "Agregar" })
      : t("menu.public.cart.add", { defaultValue: "Agregar" });

  return (
    <motion.article
      animate={
        inCart ? { boxShadow: "0 0 0 1px var(--primary)" } : { boxShadow: "0 0 0 0 transparent" }
      }
      className={`group flex flex-col overflow-hidden rounded-xl border border-border bg-card shadow-sm ${isUnavailable ? "opacity-50" : ""}`}
      transition={{ duration: 0.25, ease: "easeOut" }}
    >
      <div className="relative">
        {product.imageKey ? (
          <img
            alt={product.name}
            className="aspect-[4/3] w-full object-cover"
            height={300}
            loading="lazy"
            src={`${UPLOADTHING_BASE_URL}/${product.imageKey}`}
            width={400}
          />
        ) : (
          <div className="flex aspect-[4/3] w-full items-center justify-center bg-muted">
            <UtensilsCrossed className="h-12 w-12 text-muted-foreground/30" />
          </div>
        )}
        {hasPromotion ? (
          <span className="absolute top-3 left-3 rounded-lg bg-destructive px-2.5 py-1 font-semibold text-destructive-foreground text-xs shadow-sm">
            -{product.discountPercentage}%
          </span>
        ) : null}
        {isUnavailable ? (
          <span className="absolute top-3 right-3 rounded-lg bg-muted px-2.5 py-1 font-semibold text-muted-foreground text-xs shadow-sm">
            {unavailableLabel}
          </span>
        ) : null}
      </div>
      <div className="flex flex-1 flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-semibold text-lg leading-snug tracking-tight">{product.name}</h3>
          <div className="flex shrink-0 flex-col items-end gap-0.5">
            {hasPromotion ? (
              <>
                <span className="text-muted-foreground text-xs line-through tabular-nums">
                  {formatPrice(product.originalPrice, currency)}
                </span>
                <span className="font-semibold text-primary tabular-nums">
                  {formatPrice(product.discountedPrice ?? product.originalPrice, currency)}
                </span>
                {promotionMinQuantity ? (
                  <span className="text-muted-foreground text-[11px] leading-none">
                    {t("menu.public.promotionMinQuantity", {
                      count: promotionMinQuantity,
                      defaultValue: "Desde {{count}} unidades",
                    })}
                  </span>
                ) : null}
              </>
            ) : (
              <>
                <span className="font-semibold text-primary tabular-nums">
                  {formatPrice(product.originalPrice, currency)}
                </span>
                <span aria-hidden className="invisible text-xs">
                  &nbsp;
                </span>
              </>
            )}
          </div>
        </div>
        <p className="line-clamp-2 min-h-[2lh] text-muted-foreground text-base leading-relaxed">
          {product.description}
        </p>
        {canOrder && !isUnavailable ? (
          <AnimatePresence initial={false} mode="wait">
            {inCart ? (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mt-auto flex items-center justify-between gap-2"
                exit={{ opacity: 0, y: -4 }}
                initial={{ opacity: 0, y: 4 }}
                key="qty"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Button
                  aria-label={quantity === 1 ? removeLabel : decrementLabel}
                  onClick={() => onDecrement(product.id)}
                  size="icon"
                  type="button"
                  variant="outline"
                >
                  {quantity === 1 ? <Trash2 className="h-4 w-4" /> : <Minus className="h-4 w-4" />}
                </Button>
                <div className="relative flex h-9 flex-1 items-center justify-center overflow-hidden">
                  <AnimatePresence initial={false} mode="popLayout">
                    <motion.span
                      animate={{ opacity: 1, y: 0 }}
                      className="font-semibold text-base tabular-nums"
                      exit={{ opacity: 0, y: -10 }}
                      initial={{ opacity: 0, y: 10 }}
                      key={quantity}
                      transition={{ duration: 0.18, ease: "easeOut" }}
                    >
                      {quantity}
                    </motion.span>
                  </AnimatePresence>
                </div>
                <Button
                  aria-label={incrementLabel}
                  onClick={() => onAdd(product)}
                  size="icon"
                  type="button"
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </motion.div>
            ) : (
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                className="mt-auto"
                exit={{ opacity: 0, y: -4 }}
                initial={{ opacity: 0, y: 4 }}
                key="add"
                transition={{ duration: 0.18, ease: "easeOut" }}
              >
                <Button
                  className="w-full"
                  onClick={() => onAdd(product)}
                  size="sm"
                  type="button"
                  variant="outline"
                >
                  <Plus className="mr-1 h-4 w-4" />
                  {addLabel}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        ) : null}
      </div>
    </motion.article>
  );
}

function CategoryNav({
  sections,
  activeId,
  onSelect,
}: {
  sections: MenuSection[];
  activeId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <nav className="scrollbar-none flex gap-2 overflow-x-auto pb-2">
      {sections.map((section) => {
        const Icon = getSectionIcon(section.slug);
        const isActive = activeId === section.id;
        return (
          <button
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-medium text-sm transition-all ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent"
            }`}
            key={section.id}
            onClick={() => onSelect(section.id)}
            type="button"
          >
            <Icon className="h-4 w-4" />
            {section.name}
          </button>
        );
      })}
    </nav>
  );
}

function SectionBlock({
  model,
  section,
  actions,
}: {
  model: PublicMenuFlowModel;
  section: MenuSection;
  actions: PublicMenuFlowActions;
}) {
  const { t } = useTranslation();
  const products = model.productsBySection.get(section.id) ?? [];

  return (
    <section className="scroll-mt-24 space-y-6" id={`section-${section.id}`}>
      <div className="space-y-1">
        <h2 className="font-semibold font-serif text-2xl tracking-tight">{section.name}</h2>
        {section.description ? (
          <p className="max-w-prose text-muted-foreground italic leading-relaxed">
            {section.description}
          </p>
        ) : null}
      </div>
      {products.length > 0 ? (
        <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((product) => (
            <ProductCard
              canOrder={model.productOrderingEnabled}
              currency={model.currency}
              key={product.id}
              onAdd={actions.addProduct}
              onDecrement={actions.decrementProduct}
              product={product}
              quantity={model.quantitiesByProductId.get(product.id) ?? 0}
              variant={model.variant}
            />
          ))}
        </div>
      ) : (
        <p className="py-8 text-center text-muted-foreground text-sm italic">
          {t("menu.public.noProducts")}
        </p>
      )}
    </section>
  );
}

function FulfillmentSelector({
  enabledTypes,
  current,
  onChange,
}: {
  enabledTypes: FulfillmentType[];
  current: FulfillmentType;
  onChange: (type: FulfillmentType) => void;
}) {
  const { t } = useTranslation();

  return (
    <div
      aria-label={t("delivery.fulfillment.label", { defaultValue: "Fulfillment" })}
      className="flex gap-2"
      role="tablist"
    >
      {enabledTypes.map((type) => {
        const isActive = type === current;

        return (
          <button
            aria-selected={isActive}
            className={`flex shrink-0 items-center gap-2 rounded-full px-4 py-2 font-medium text-sm transition-all ${
              isActive
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent"
            }`}
            key={type}
            onClick={() => onChange(type)}
            role="tab"
            type="button"
          >
            {t(fulfillmentTypeLabelKey(type), { defaultValue: type })}
          </button>
        );
      })}
    </div>
  );
}

function SupermarketProductRow({
  model,
  actions,
  title,
  description,
  products,
}: {
  model: PublicMenuFlowModel;
  actions: PublicMenuFlowActions;
  title: string;
  description?: string | null;
  products: PublicMenuProduct[];
}) {
  const previewProducts = products.slice(0, 10);

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="font-semibold text-lg tracking-tight">{title}</h3>
        {description ? (
          <p className="max-w-2xl text-muted-foreground text-sm leading-relaxed">{description}</p>
        ) : null}
      </div>
      <div className="-mx-4 overflow-x-auto overscroll-x-contain px-4 pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:px-0">
        <div className="flex snap-x snap-mandatory gap-4">
          {previewProducts.map((product) => (
            <div className="w-44 shrink-0 snap-start sm:w-56" key={product.id}>
              <ProductCard
                canOrder={model.productOrderingEnabled}
                currency={model.currency}
                onAdd={actions.addProduct}
                onDecrement={actions.decrementProduct}
                product={product}
                quantity={model.quantitiesByProductId.get(product.id) ?? 0}
                variant={model.variant}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SupermarketCategoryBlock({
  model,
  actions,
  category,
}: {
  model: PublicMenuFlowModel;
  actions: PublicMenuFlowActions;
  category: SupermarketCategory;
}) {
  const { t } = useTranslation();
  const visibleSubcategories = category.subcategories.filter(
    (subcategory) => subcategory.products.length > 0,
  );

  return (
    <section className="scroll-mt-24 space-y-6" id={`section-${category.section.id}`}>
      <div className="space-y-1">
        <h2 className="font-semibold font-serif text-2xl tracking-tight">
          {category.section.name}
        </h2>
        {category.section.description ? (
          <p className="max-w-prose text-muted-foreground italic leading-relaxed">
            {category.section.description}
          </p>
        ) : null}
      </div>

      <div className="space-y-8">
        {category.directProducts.length > 0 ? (
          <SupermarketProductRow
            actions={actions}
            model={model}
            products={category.directProducts}
            title={t("menu.public.catalog.allCategory", {
              defaultValue: "Toda la categoría",
            })}
          />
        ) : null}

        {visibleSubcategories.map((subcategory) => (
          <SupermarketProductRow
            actions={actions}
            description={subcategory.section.description}
            key={subcategory.section.id}
            model={model}
            products={subcategory.products}
            title={subcategory.section.name}
          />
        ))}
      </div>
    </section>
  );
}

function cartRowLabel(kind: PublicMenuFlowModel["cartDrawer"]["rows"][number]["kind"]) {
  if (kind === "delivery-fee") {
    return {
      key: "delivery.checkout.deliveryFee",
      defaultValue: "Costo de envío",
    };
  }
  if (kind === "total") {
    return {
      key: "delivery.cart.total",
      defaultValue: "Total",
    };
  }
  return {
    key: "menu.public.cart.subtotal",
    defaultValue: "Subtotal",
  };
}

function CartDrawer({
  model,
  actions,
}: {
  model: PublicMenuFlowModel;
  actions: PublicMenuFlowActions;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!model.cartDrawer.visible) {
    return null;
  }

  const title =
    model.variant === "delivery"
      ? t("delivery.cart.title", { defaultValue: "Tu pedido" })
      : t("menu.public.cart.title", { defaultValue: "Tu pedido" });
  const description =
    model.variant === "delivery"
      ? t("delivery.cart.description", { defaultValue: "Revisá los items antes de continuar." })
      : t("menu.public.cart.description", {
          defaultValue: "Revisá los items antes de continuar.",
        });
  const checkoutLabel =
    model.variant === "delivery"
      ? t("delivery.cart.checkout", { defaultValue: "Ir a pagar" })
      : t("menu.public.cart.checkout", { defaultValue: "Ir a pagar" });

  return (
    <Sheet onOpenChange={setOpen} open={open}>
      <motion.div
        animate={{ opacity: 1, y: 0, scale: 1 }}
        className="fixed right-4 bottom-6 z-20 sm:right-8"
        initial={{ opacity: 0, y: 24, scale: 0.9 }}
        transition={{ type: "spring", stiffness: 320, damping: 24 }}
      >
        <SheetTrigger asChild>
          <Button className="shadow-lg" size="lg" type="button">
            <ShoppingCart className="mr-2 h-5 w-5" />
            <motion.span
              animate={{ scale: [1, 1.12, 1] }}
              className="inline-flex items-baseline gap-1 tabular-nums"
              key={model.cart.itemCount}
              transition={{ duration: 0.32, ease: "easeOut" }}
            >
              {model.cart.itemCount} · {formatPrice(model.cart.subtotal, model.currency)}
            </motion.span>
          </Button>
        </SheetTrigger>
      </motion.div>
      <SheetContent className="flex w-full flex-col px-5 sm:max-w-md">
        <SheetHeader>
          <SheetTitle className="text-2xl">{title}</SheetTitle>
          <SheetDescription className="text-lg">{description}</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto p-4">
          {model.cart.lines.map((line) => {
            const unitPrice = lineUnitPrice(line);
            const missingPromotionQuantity = line.promotionMinQuantity
              ? line.promotionMinQuantity - line.quantity
              : 0;

            return (
              <div
                className="flex items-center gap-3 rounded-lg border border-border p-3"
                key={line.productId}
              >
                <div className="flex-1">
                  <div className="font-medium text-sm">{line.name}</div>
                  <div className="text-muted-foreground text-xs">
                    {formatPrice(unitPrice, model.currency)} ·{" "}
                    {formatPrice(lineSubtotal(line), model.currency)}
                  </div>
                  {missingPromotionQuantity > 0 && line.discountPercentage ? (
                    <div className="text-primary text-xs">
                      {t("menu.public.cart.promotionMinQuantityHint", {
                        count: missingPromotionQuantity,
                        discount: line.discountPercentage,
                        defaultValue: "Agregá {{count}} más para activar -{{discount}}%",
                      })}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    aria-label={t("delivery.cart.decrement", { defaultValue: "Restar" })}
                    onClick={() => actions.setQuantity(line.productId, line.quantity - 1)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <Minus className="h-3 w-3" />
                  </Button>
                  <span className="w-6 text-center font-medium text-sm tabular-nums">
                    {line.quantity}
                  </span>
                  <Button
                    aria-label={t("delivery.cart.increment", { defaultValue: "Sumar" })}
                    onClick={() => actions.setQuantity(line.productId, line.quantity + 1)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <Button
                    aria-label={t("delivery.cart.remove", { defaultValue: "Quitar" })}
                    onClick={() => actions.removeLine(line.productId)}
                    size="icon"
                    type="button"
                    variant="ghost"
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <SheetFooter className="flex-col gap-2 sm:flex-col">
          {model.cartDrawer.rows.map((row) => {
            const label = cartRowLabel(row.kind);
            return (
              <div
                className={`flex items-center justify-between ${row.emphasized ? "font-semibold" : "text-sm"}`}
                key={row.kind}
              >
                <span>{t(label.key, { defaultValue: label.defaultValue })}</span>
                <span className="tabular-nums">{formatPrice(row.amount, model.currency)}</span>
              </div>
            );
          })}
          {model.cartDrawer.blockers.map((blocker) => {
            if (blocker.kind === "minimum-order") {
              return (
                <p className="text-destructive text-xs" key={blocker.kind}>
                  {t("delivery.checkout.minOrderError", {
                    amount: formatPrice(blocker.amount, model.currency),
                  })}
                </p>
              );
            }

            return (
              <p className="text-destructive text-xs" key={blocker.kind}>
                {t("delivery.checkout.noPaymentMethod", {
                  defaultValue:
                    "Este restaurante no tiene medios de pago disponibles para delivery.",
                })}
              </p>
            );
          })}
          <Button
            className="w-full"
            disabled={!model.cartDrawer.canCheckout}
            onClick={() => {
              setOpen(false);
              actions.checkout();
            }}
            size="lg"
            type="button"
          >
            {checkoutLabel}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function LoadingState() {
  return (
    <div className="mx-auto flex min-h-screen w-full max-w-5xl flex-col gap-8 px-4 py-12 sm:px-8">
      <Skeleton className="h-8 w-32" />
      <Skeleton className="h-12 w-64" />
      <div className="flex gap-3">
        <Skeleton className="h-10 w-28 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
        <Skeleton className="h-10 w-28 rounded-full" />
      </div>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-72 w-full" />
      </div>
    </div>
  );
}

function ErrorState({ flow }: { flow: Extract<PublicMenuFlowState, { status: "error" }> }) {
  const { t } = useTranslation();
  const title =
    flow.variant === "delivery"
      ? t("delivery.menu.unavailableTitle", { defaultValue: "Delivery no disponible" })
      : t("menu.public.title");
  const message =
    flow.message ??
    (flow.variant === "delivery"
      ? t("delivery.menu.unavailableTitle", { defaultValue: "Delivery no disponible" })
      : t("menu.public.loadError"));

  return (
    <div className="mx-auto flex min-h-screen w-full max-w-5xl items-center justify-center px-4 py-12 sm:px-8">
      <div className="space-y-3 text-center">
        <h1 className="font-semibold font-serif text-2xl">{title}</h1>
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}

function EmptyState() {
  const { t } = useTranslation();

  return (
    <div className="flex flex-1 items-center justify-center py-20">
      <div className="space-y-3 text-center">
        <Bookmark className="mx-auto h-10 w-10 text-muted-foreground/40" />
        <h2 className="font-semibold font-serif text-xl">{t("menu.public.emptyTitle")}</h2>
        <p className="text-muted-foreground text-sm">{t("menu.public.emptyDescription")}</p>
      </div>
    </div>
  );
}

function ReadyState({
  flow,
  onFulfillmentChange,
}: {
  flow: Extract<PublicMenuFlowState, { status: "ready" }>;
  onFulfillmentChange?: (type: FulfillmentType) => void;
}) {
  const { t } = useTranslation();
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);
  const { model, actions } = flow;
  const pageId = model.variant === "delivery" ? "delivery-menu-content" : "public-menu-content";
  const isSupermarket = model.organization.businessType === "supermarket";

  const supermarketCategories = useMemo<SupermarketCategory[]>(() => {
    if (!isSupermarket) {
      return [];
    }
    const products = Array.from(model.productsBySection.values()).flat();
    return buildSupermarketCatalog(model.sectionsForList, products).filter(
      (category) => category.productCount > 0,
    );
  }, [isSupermarket, model.sectionsForList, model.productsBySection]);

  const navSections = isSupermarket
    ? supermarketCategories.map((category) => category.section)
    : model.sectionsForNav;

  const handleSectionSelect = (id: string) => {
    setActiveSectionId(id);
    document
      .getElementById(`section-${id}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      className="mx-auto flex min-h-screen w-full max-w-5xl flex-col px-4 py-12 sm:px-8"
      id={pageId}
    >
      <header className="space-y-4 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Badge
              className="rounded-full font-mono text-xs uppercase tracking-widest"
              variant="secondary"
            >
              {model.variant === "delivery"
                ? t("delivery.fulfillment.delivery", { defaultValue: "Delivery" })
                : t("menu.public.menuLabel", { defaultValue: "Menu" })}
            </Badge>
            {model.variant === "dine-in" && model.tableLabel ? (
              <Badge className="rounded-full text-xs" variant="default">
                {model.tableLabel}
              </Badge>
            ) : null}
            {model.variant === "delivery" && model.deliveryMinimumOrder ? (
              <Badge className="rounded-full text-xs" variant="default">
                {t("delivery.menu.minimumOrder", {
                  amount: formatPrice(model.deliveryMinimumOrder, model.currency),
                  defaultValue: "Pedido mínimo {{amount}}",
                })}
              </Badge>
            ) : null}
          </div>
          {model.variant === "delivery" ? <DeliveryLoginCta /> : null}
        </div>
        <h1 className="font-bold font-serif text-4xl tracking-tight sm:text-5xl">
          {model.organization.name}
        </h1>
        {model.variant === "delivery" ? (
          <p className="text-muted-foreground text-sm">
            {t("delivery.menu.subtitle", { defaultValue: "Pedí delivery a tu domicilio" })}
          </p>
        ) : null}
        {model.variant === "dine-in" && model.guestError ? (
          <p className="text-destructive text-sm">{model.guestError}</p>
        ) : null}
        {model.showScanToOrder ? (
          <p className="text-muted-foreground text-sm">
            {t("menu.public.scanToOrder", {
              defaultValue: "Escaneá el QR de tu mesa para hacer un pedido.",
            })}
          </p>
        ) : null}
        {model.selector.visible && onFulfillmentChange ? (
          <FulfillmentSelector
            current={model.selector.current}
            enabledTypes={model.selector.enabledTypes}
            onChange={onFulfillmentChange}
          />
        ) : null}
      </header>

      {navSections.length > 1 ? (
        <div className="sticky top-0 z-10 -mx-4 bg-background/85 px-4 py-3 backdrop-blur-lg sm:-mx-8 sm:px-8">
          <CategoryNav
            activeId={activeSectionId}
            onSelect={handleSectionSelect}
            sections={navSections}
          />
        </div>
      ) : null}

      {isSupermarket ? (
        supermarketCategories.length > 0 ? (
          <div className="space-y-16 pt-8">
            {supermarketCategories.map((category) => (
              <SupermarketCategoryBlock
                actions={actions}
                category={category}
                key={category.section.id}
                model={model}
              />
            ))}
          </div>
        ) : (
          <EmptyState />
        )
      ) : model.sectionsForList.length > 0 ? (
        <div className="space-y-16 pt-8">
          {model.sectionsForList.map((section) => (
            <SectionBlock actions={actions} key={section.id} model={model} section={section} />
          ))}
        </div>
      ) : (
        <EmptyState />
      )}

      <CartDrawer actions={actions} model={model} />
    </div>
  );
}

export function PublicMenuFlowView({
  flow,
  onFulfillmentChange,
}: {
  flow: PublicMenuFlowState;
  onFulfillmentChange?: (type: FulfillmentType) => void;
}) {
  if (flow.status === "loading") {
    return <LoadingState />;
  }

  if (flow.status === "error") {
    return <ErrorState flow={flow} />;
  }

  if (flow.status === "idle") {
    return null;
  }

  return <ReadyState flow={flow} onFulfillmentChange={onFulfillmentChange} />;
}
